import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  signal,
  computed,
  inject,
  ElementRef,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ContractService } from '../service/contract.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DatePickerModule } from 'primeng/datepicker';
import { MessageService } from 'primeng/api';

// ─── Interfaces ───────────────────────────────────────────────────────────────

export interface ContractMetadata {
  auto_detected?: string;
  chunks_created?: string;
  company_name?: string;
  doc_type_detected?: string;
  document_id?: string;
  excel_config_used?: string;
  effective_date?: string;
  renewal_date?: string;
  termination_date?: string;
  expiry_date?: string;
  metadata_details?: string;
  summary?: string;
  termination_comments?: string;
  termination_summary?: string;
  detailed_summary?: string;
  rates_and_services?: string;
  /** Category type string e.g. "ASA", "MSA", "NDA" */
  category_type?: string;
  /** Auto-renewal flag stored as string "true"/"false" or boolean */
  is_auto_renewal?: string | boolean;
  /** Auto-renewal summary / comments */
  auto_renewal_summary?: string;
  comments?: string;
}

export interface ContractApiItem {
  id: number;
  title: string;
  doc_name: string;
  doc_type: string;
  doc_size: string;
  rfp_type: string;
  rfp_type_id: number;
  status: string;
  status_id: number;
  created_by: string;
  created_by_id: number;
  created_at: string;
  updated_at: string;
  termination_date: string | null;
  expiry_date: string | null;
  version_no: number;
  is_expired: boolean;
  is_head_version: boolean;
  kb_doc_ids: null;
  sheet_details: unknown[];
  client_id: number;
  metadata: ContractMetadata;
  contract_status?: string; // "Active" | "Expired" | "Terminated"
}

export interface TableRow {
  id: number;
  client_id: number;
  contractName: string;
  companyName: string;
  effectiveDate: string;
  renewalDate: string;
  terminationDate: string;
  expiryDate: string;
  terminationSummary: string;
  isExpired: boolean;
  docType: string;
  categoryType: string;
  isAutoRenewal: boolean | null;
  autoRenewalSummary: string;
  /** Full metadata preserved for the preview modal. */
  metadata: ContractMetadata;
  contract_status?: string; // "Active" | "Expired" | "Terminated"
  comments: string;
}

export type SortableField = keyof Pick<
  TableRow,
  | 'contractName'
  | 'companyName'
  | 'effectiveDate'
  | 'renewalDate'
  | 'terminationDate'
  | 'expiryDate'
  | 'isExpired'
  | 'categoryType'
>;

export type PreviewTab =
  | 'summary'
  | 'detailed_summary'
  | 'metadata_details'
  | 'rates_and_services';

/** Shape of a filter payload item sent to the API. */
interface ApiFilter {
  property: string;
  operator: string;
  value: unknown;
}

interface CompanyRecord {
  id: number | string;
  company_name?: string;
}

interface PaginatedResponse {
  pagination?: { total?: number | string };
  data?: ContractApiItem[];
}

export interface RowGroup {
  companyName: string;
  rows: TableRow[];
  expanded: boolean;
}

// ─── Data helpers ─────────────────────────────────────────────────────────────

function extractMetaField(html: string | undefined, label: string): string {
  if (!html) return '';
  const regex = new RegExp(`<strong>${label}:<\\/strong>\\s*([^<]+)`);
  const match = html.match(regex);
  return match?.[1]?.trim() ?? '';
}

function stripHtml(html: string): string {
  return html
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// function parseAutoRenewal(val: string | boolean | undefined): boolean | null {
//   if (val === undefined || val === null || val === '') return null;
//   if (typeof val === 'boolean') return val;
//   return val.toString().toLowerCase() === 'true';
// }

function parseAutoRenewal(val: string | boolean | undefined): boolean | null {
  if (val === undefined || val === null || val === '') return null;
  if (typeof val === 'boolean') return val;
  const lower = val.toString().toLowerCase();
  // handle both "true"/"false" and "yes"/"no"
  return lower === 'true' || lower === 'yes';
}

/** Convert a Date object to "MM-DD-YYYY" string for API payloads */
function dateToApiString(date: Date | null | undefined): string {
  if (!date) return '';
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const y = date.getFullYear();
  return `${m}-${d}-${y}`;
}

export function mapToTableRow(
  item: ContractApiItem,
  companyMap: Map<number, string>,
): TableRow {
  const details = item.metadata?.metadata_details ?? '';
  const effectiveDate = item.metadata?.effective_date ?? '';
  // const effectiveDate =
  //   item.metadata?.effective_date ||
  //   extractMetaField(details, 'Effective Date') ||
  //   '';
  const renewalDate = item?.metadata?.renewal_date ?? '';
  // const renewalDate =
  //   item.metadata?.renewal_date ||
  //   extractMetaField(details, 'Renewal Date') ||
  //   '';
  const terminationDate = item?.metadata?.termination_date ?? '';
  const expiryDate = item?.metadata?.expiry_date ?? '';
  // const terminationDate =
  //   item.termination_date ||
  //   item.metadata?.termination_date ||
  //   extractMetaField(details, 'Termination Date') ||
  //   '';

  const companyIdRaw = item.metadata?.company_name;
  let companyName = '';
  if (companyIdRaw) {
    const numId = parseInt(companyIdRaw, 10);
    if (!isNaN(numId)) {
      companyName = companyMap.get(numId) ?? '';
    }
  }
  if (!companyName) companyName = extractMetaField(details, 'Company');
  if (!companyName) companyName = extractMetaField(details, 'Client Name');
  if (!companyName) companyName = item.title;

  // const terminationSummary = item.metadata?.termination_comments
  //   ? stripHtml(item.metadata.termination_comments)
  //   : '';

  const terminationSummary = item.metadata?.termination_summary
    ? stripHtml(item.metadata.termination_summary)
    : item.metadata?.termination_comments
      ? stripHtml(item.metadata.termination_comments)
      : '';

  const autoRenewalSummary = item.metadata?.auto_renewal_summary
    ? stripHtml(item.metadata.auto_renewal_summary)
    : '';

  return {
    id: item.id,
    client_id: item.client_id,
    contractName: item.title ?? '',
    companyName,
    effectiveDate,
    renewalDate,
    terminationDate,
    expiryDate,
    terminationSummary,
    isExpired: item.is_expired,
    docType: item.doc_type?.toUpperCase() ?? '',
    categoryType: item.metadata?.category_type ?? '',
    isAutoRenewal: parseAutoRenewal(item.metadata?.is_auto_renewal),
    autoRenewalSummary,
    metadata: item.metadata ?? {},
    contract_status:
      item?.contract_status ?? (item.is_expired ? 'Expired' : 'Active'),
    comments: item.metadata?.comments // ← direct metadata field
      ? stripHtml(item.metadata.comments)
      : extractMetaField(details, 'Comments') || // ← fallback: parse from metadata_details HTML
        '',
  };
}

// ─── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_EXPIRY_PRESET = 'all';

// ─── Component ────────────────────────────────────────────────────────────────

@Component({
  selector: 'app-contracts-table',
  standalone: true,
  imports: [CommonModule, FormsModule, DatePickerModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './contracts-filter.component.html',
  styleUrl: './contracts-filter.component.scss',
})
export class ContractsTableComponent implements OnInit, OnDestroy {
  private readonly contractService = inject(ContractService);
  private readonly msgService = inject(MessageService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  @ViewChild('companyDropdownHost', { read: ElementRef })
  private companyDropdownHostRef?: ElementRef<HTMLElement>;

  // ── Pending (UI) state ────────────────────────────────────────────────────
  pendingCompanies: string[] = [];
  pendingCategoryType = '';
  pendingAutoRenewal = ''; // '' | 'true' | 'false'
  pendingExpiryPreset = DEFAULT_EXPIRY_PRESET; // '' = custom, '30' = days
  pendingDateFromObj: Date | null = null; // bound to PrimeNG DatePicker
  pendingDateToObj: Date | null = null; // bound to PrimeNG DatePicker

  // ── Applied (API) state ───────────────────────────────────────────────────
  appliedCompanies: string[] = [];
  appliedCategoryType = '';
  appliedAutoRenewal = '';
  appliedExpiryPreset = DEFAULT_EXPIRY_PRESET;
  appliedDateFrom = ''; // MM-DD-YYYY string for API
  appliedDateTo = ''; // MM-DD-YYYY string for API

  // ── Company dropdown ──────────────────────────────────────────────────────
  companyDropdownOpen = false;
  readonly companySearchText = signal('');

  // ── Pagination ────────────────────────────────────────────────────────────
  pageSizeValue = 25;
  private readonly _currentPage = signal(1);
  private readonly _pageSize = signal(25);
  private readonly _apiTotal = signal(0);
  readonly apiTotal = this._apiTotal.asReadonly();

  // ── Sort ──────────────────────────────────────────────────────────────────
  private readonly _sortField = signal<SortableField | ''>('');
  private readonly _sortDir = signal<'asc' | 'desc'>('asc');

  // ── Loading / error ───────────────────────────────────────────────────────
  private readonly _loading = signal(true);
  private readonly _error = signal('');

  // ── Data ──────────────────────────────────────────────────────────────────
  private readonly _rows = signal<TableRow[]>([]);
  private readonly _categoryTypes = signal<string[]>([]);
  private readonly _companyNames = signal<string[]>([]);

  private companyNameToId = new Map<string, number>();

  // ── Public readonly signals ───────────────────────────────────────────────
  readonly currentPage = this._currentPage.asReadonly();
  readonly pageSize = this._pageSize.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly categoryTypes = this._categoryTypes.asReadonly();
  readonly companyNames = this._companyNames.asReadonly();
  readonly rows = this._rows.asReadonly();

  pendingContractStatus = '';
  appliedContractStatus = '';

  // edit modal state
  editRow: TableRow | null = null;
  editForm = {
    terminationDate: null as Date | null,
    expiryDate: null as Date | null,
    renewalDate: null as Date | null,
    isAutoRenewal: '' as '' | 'yes' | 'no',
    comments: '',
    terminationSummary: '',
    autoRenewalSummary: '',
  };
  editSaving = false;

  readonly filteredCompanyOptions = computed(() => {
    const search = this.companySearchText().toLowerCase().trim();
    return search
      ? this._companyNames().filter((c) => c.toLowerCase().includes(search))
      : this._companyNames();
  });

  // ── Preview modal state ───────────────────────────────────────────────────
  previewRow: TableRow | null = null;
  modalFullscreen = false;
  activeTab: PreviewTab = 'summary';

  readonly previewTabs: { key: PreviewTab; label: string }[] = [
    { key: 'summary', label: 'Summary' },
    { key: 'detailed_summary', label: 'Detailed Summary' },
    { key: 'metadata_details', label: 'Meta Data' },
    { key: 'rates_and_services', label: 'Rate and Services' },
  ];

  // ── Derived ───────────────────────────────────────────────────────────────
  readonly totalPages = computed(() =>
    Math.max(1, Math.ceil(this._apiTotal() / this._pageSize())),
  );

  readonly sortedRows = computed(() => {
    const sf = this._sortField();
    const sd = this._sortDir();
    const src = this._rows();
    if (!sf) return src;
    return [...src].sort((a, b) => {
      const av = String(a[sf] ?? '');
      const bv = String(b[sf] ?? '');
      return sd === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  });

  readonly visiblePages = computed((): number[] => {
    const total = this.totalPages();
    const cur = this._currentPage();
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages: number[] = [1];
    if (cur > 3) pages.push(-1);
    for (let p = Math.max(2, cur - 1); p <= Math.min(total - 1, cur + 1); p++) {
      pages.push(p);
    }
    if (cur < total - 2) pages.push(-1);
    pages.push(total);
    return pages;
  });

  // Signal to track which groups are collapsed (set of companyName strings)
  private readonly _collapsedGroups = signal<Set<string>>(new Set());

  readonly groupedRows = computed((): RowGroup[] => {
    const sf = this._sortField();
    const sd = this._sortDir();
    const src = this._rows();
    const collapsed = this._collapsedGroups();

    // Sort within each company group
    const sorted = sf
      ? [...src].sort((a, b) => {
          const av = String(a[sf] ?? '');
          const bv = String(b[sf] ?? '');
          return sd === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
        })
      : src;

    // Group by companyName
    const map = new Map<string, TableRow[]>();
    for (const row of sorted) {
      const key = row.companyName || '—';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(row);
    }

    // Sort groups alphabetically by company name
    return [...map.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([companyName, rows]) => ({
        companyName,
        rows,
        expanded: !collapsed.has(companyName),
      }));
  });

  toggleGroup(companyName: string): void {
    this._collapsedGroups.update((set) => {
      const next = new Set(set);
      next.has(companyName) ? next.delete(companyName) : next.add(companyName);
      return next;
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  ngOnInit(): void {
    this.contractService
      .getCompanies()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (companies: { result?: CompanyRecord[] }) => {
          const companyList = companies?.result ?? [];
          const companyMap = new Map<number, string>();
          this.companyNameToId = new Map<string, number>();

          for (const c of companyList) {
            const id = Number(c.id);
            const name = String(c.company_name ?? '');
            if (id && name) {
              companyMap.set(id, name);
              this.companyNameToId.set(name, id);
            }
          }

          this._companyNames.set(
            [...companyMap.values()].filter(Boolean).sort(),
          );

          this.loadContracts(companyMap);
        },
        error: (err: unknown) => {
          console.error('[ContractsTable] Failed to load companies:', err);
          this._error.set('Failed to load Contracts. Please try again.');
          this._loading.set(false);
          this.cdr.markForCheck();
        },
      });

    document.addEventListener('click', this.onDocumentClick, true);
    document.addEventListener('keydown', this.onKeyDown, true);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    document.removeEventListener('click', this.onDocumentClick, true);
    document.removeEventListener('keydown', this.onKeyDown, true);
    document.body.style.overflow = '';
  }

  // ══════════════════════════════════════════════════════════════════════════
  // C O R E   A P I   L O A D
  // ══════════════════════════════════════════════════════════════════════════

  private buildFilters(): ApiFilter[] {
    const filters: ApiFilter[] = [];

    // ── 1. Termination date ────────────────────────────────────────────────
    if (this.appliedExpiryPreset === 'all') {
      filters.push({
        property: 'meta:expiry_date',
        operator: 'eq',
        value: 'all',
      });
    } else if (this.appliedExpiryPreset !== '') {
      filters.push({
        property: 'meta:expiry_date',
        operator: 'eq',
        value: this.appliedExpiryPreset,
      });
    } else if (this.appliedDateFrom || this.appliedDateTo) {
      filters.push({
        property: 'meta:expiry_date',
        operator: 'between',
        value: [this.appliedDateFrom, this.appliedDateTo].filter(Boolean),
      });
    } else {
      filters.push({
        property: 'meta:expiry_date',
        operator: 'eq',
        value: 'all',
      });
    }

    // ── 2. Company ─────────────────────────────────────────────────────────
    if (this.appliedCompanies.length > 0) {
      const clientIds = this.appliedCompanies
        .map((name) => this.companyNameToId.get(name))
        .filter((id): id is number => id !== undefined);

      if (clientIds.length > 0) {
        filters.push({
          property: 'meta:company_name',
          operator: clientIds.length === 1 ? 'eq' : 'in',
          value: clientIds.length === 1 ? clientIds[0] : clientIds,
        });
      }
    }

    // ── 3. Category type ───────────────────────────────────────────────────
    if (this.appliedCategoryType) {
      filters.push({
        property: 'meta:category_type',
        operator: 'eq',
        value: this.appliedCategoryType,
      });
    }

    // ── 4. Auto renewal ────────────────────────────────────────────────────
    if (this.appliedAutoRenewal !== '') {
      filters.push({
        property: 'meta:is_auto_renewal',
        operator: 'eq',
        value: this.appliedAutoRenewal?.toLowerCase() === 'yes' ? 'Yes' : 'No',
      });
    }
    // contract status
    if (this.appliedContractStatus) {
      filters.push({
        property: 'meta:contract_status',
        operator: 'eq',
        value: this.appliedContractStatus,
      });
    }

    return filters;
  }

  private loadContracts(companyMap?: Map<number, string>): void {
    this._loading.set(true);
    this._error.set('');
    this.cdr.markForCheck();

    const payload = {
      page: this._currentPage(),
      per_page: this._pageSize(),
      filters: this.buildFilters(),
    };

    this.contractService
      .getRFPDocuments(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (docs: PaginatedResponse) => {
          const pagination = docs?.pagination ?? {};
          this._apiTotal.set(Number(pagination.total ?? 0));

          const resolvedMap: Map<number, string> =
            companyMap ??
            new Map(
              [...this.companyNameToId.entries()].map(([name, id]) => [
                id,
                name,
              ]),
            );

          const items: ContractApiItem[] = docs?.data ?? [];
          const rows = items.map((item) => mapToTableRow(item, resolvedMap));

          this._rows.set(rows);

          const newCats = rows
            .map((r) => r.categoryType)
            .filter((c): c is string => !!c);
          const merged = [
            ...new Set([...this._categoryTypes(), ...newCats]),
          ].sort();
          this._categoryTypes.set(merged);

          this._loading.set(false);
          this.cdr.markForCheck();
        },
        error: (err: unknown) => {
          console.error('[ContractsTable] Failed to load contracts:', err);
          this._error.set('Failed to load contracts. Please try again.');
          this._loading.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  // ══════════════════════════════════════════════════════════════════════════
  // F I L T E R   A C T I O N S
  // ══════════════════════════════════════════════════════════════════════════

  applyFilters(): void {
    this.appliedCompanies = [...this.pendingCompanies];
    this.appliedCategoryType = this.pendingCategoryType;
    this.appliedAutoRenewal = this.pendingAutoRenewal;
    this.appliedExpiryPreset = this.pendingExpiryPreset;

    this.appliedDateFrom = dateToApiString(this.pendingDateFromObj);
    this.appliedDateTo = dateToApiString(this.pendingDateToObj);
    this.appliedContractStatus = this.pendingContractStatus;

    this._currentPage.set(1);
    this.loadContracts();
  }

  resetFilters(): void {
    this.pendingCompanies = [];
    this.pendingCategoryType = '';
    this.pendingAutoRenewal = '';
    this.pendingExpiryPreset = DEFAULT_EXPIRY_PRESET;
    this.pendingDateFromObj = null;
    this.pendingDateToObj = null;
    this.pendingContractStatus = '';

    this.appliedCompanies = [];
    this.appliedCategoryType = '';
    this.appliedAutoRenewal = '';
    this.appliedExpiryPreset = DEFAULT_EXPIRY_PRESET;
    this.appliedDateFrom = '';
    this.appliedDateTo = '';
    this.appliedContractStatus = '';

    this._collapsedGroups.set(new Set());

    this._currentPage.set(1);
    this.loadContracts();
  }

  hasAppliedFilters(): boolean {
    return (
      this.appliedCompanies.length > 0 ||
      !!this.appliedCategoryType ||
      this.appliedAutoRenewal !== '' ||
      this.appliedExpiryPreset !== DEFAULT_EXPIRY_PRESET ||
      !!this.appliedDateFrom ||
      !!this.appliedDateTo ||
      !!this.appliedContractStatus
    );
  }

  // removeChip(
  //   type:
  //     | 'companies'
  //     | 'category'
  //     | 'autoRenewal'
  //     | 'termination'
  //     | 'contractStatus',
  // ): void {
  //   const actions: Record<typeof type, () => void> = {
  //     companies: () => {
  //       this.pendingCompanies = [];
  //       this.appliedCompanies = [];
  //     },
  //     category: () => {
  //       this.pendingCategoryType = '';
  //       this.appliedCategoryType = '';
  //     },
  //     autoRenewal: () => {
  //       this.pendingAutoRenewal = '';
  //       this.appliedAutoRenewal = '';
  //     },
  //     termination: () => {
  //       this.pendingTerminationPreset = DEFAULT_TERMINATION_PRESET;
  //       this.pendingDateFromObj = null;
  //       this.pendingDateToObj = null;
  //       this.appliedTerminationPreset = DEFAULT_TERMINATION_PRESET;
  //       this.appliedDateFrom = '';
  //       this.appliedDateTo = '';
  //     },
  //     contractStatus: () => {
  //       this.pendingContractStatus = '';
  //       this.appliedContractStatus = '';
  //     },
  //   };

  //   actions[type]();
  //   this._currentPage.set(1);
  //   this.loadContracts();
  // }

  // Change the type union
  removeChip(
    type:
      | 'companies'
      | 'category'
      | 'autoRenewal'
      | 'expiry'
      | 'contractStatus',
  ): void {
    const actions: Record<typeof type, () => void> = {
      companies: () => {
        this.pendingCompanies = [];
        this.appliedCompanies = [];
      },
      category: () => {
        this.pendingCategoryType = '';
        this.appliedCategoryType = '';
      },
      autoRenewal: () => {
        this.pendingAutoRenewal = '';
        this.appliedAutoRenewal = '';
      },
      expiry: () => {
        this.pendingExpiryPreset = DEFAULT_EXPIRY_PRESET;
        this.pendingDateFromObj = null;
        this.pendingDateToObj = null;
        this.appliedExpiryPreset = DEFAULT_EXPIRY_PRESET;
        this.appliedDateFrom = '';
        this.appliedDateTo = '';
      },
      contractStatus: () => {
        this.pendingContractStatus = '';
        this.appliedContractStatus = '';
      },
    };
    actions[type]();
    this._currentPage.set(1);
    this.loadContracts();
  }

  expiryChipLabel(): string {
    if (this.appliedExpiryPreset === 'all') return 'Expiry: All';
    if (this.appliedExpiryPreset !== '') {
      const days = Number(this.appliedExpiryPreset);

      return days >= 0
        ? `Expiry: Next ${days} days`
        : `Expiry: Past ${Math.abs(days)} days`;
    }
    if (this.appliedDateFrom && this.appliedDateTo) {
      return `Expiry: ${this.formatDate(this.appliedDateFrom)} – ${this.formatDate(this.appliedDateTo)}`;
    }
    if (this.appliedDateFrom)
      return `Expiry from: ${this.formatDate(this.appliedDateFrom)}`;
    if (this.appliedDateTo)
      return `Expiry to: ${this.formatDate(this.appliedDateTo)}`;
    return 'Expiry Date';
  }

  onExpiryPresetChange(val: string): void {
    this.pendingExpiryPreset = val;
    if (val !== '') {
      this.pendingDateFromObj = null;
      this.pendingDateToObj = null;
    }
  }

  // ── Company dropdown ──────────────────────────────────────────────────────
  toggleCompanyDropdown(): void {
    this.companyDropdownOpen = !this.companyDropdownOpen;
    if (this.companyDropdownOpen) {
      this.companySearchText.set('');
    }
  }

  onCompanySearch(event: Event): void {
    this.companySearchText.set((event.target as HTMLInputElement).value);
  }

  clearCompanySearch(): void {
    this.companySearchText.set('');
  }

  isPendingCompanySelected(co: string): boolean {
    return this.pendingCompanies.includes(co);
  }

  togglePendingCompany(co: string): void {
    this.pendingCompanies = this.isPendingCompanySelected(co)
      ? this.pendingCompanies.filter((c) => c !== co)
      : [...this.pendingCompanies, co];
  }

  selectAllCompanies(): void {
    this.pendingCompanies = [...this.filteredCompanyOptions()];
  }

  clearPendingCompanies(): void {
    this.pendingCompanies = [];
  }

  private readonly onDocumentClick = (event: MouseEvent): void => {
    if (
      this.companyDropdownOpen &&
      this.companyDropdownHostRef &&
      !this.companyDropdownHostRef.nativeElement.contains(event.target as Node)
    ) {
      this.companyDropdownOpen = false;
      this.cdr.markForCheck();
    }
  };

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape') return;
    if (this.previewRow) {
      this.closePreview();
    } else if (this.companyDropdownOpen) {
      this.companyDropdownOpen = false;
      this.cdr.markForCheck();
    }
  };

  // ── Pagination ────────────────────────────────────────────────────────────
  onPageSizeChange(val: number): void {
    this._pageSize.set(Number(val));
    this._currentPage.set(1);
    this.loadContracts();
  }

  goToPage(p: number): void {
    const clamped = Math.max(1, Math.min(p, this.totalPages()));
    if (clamped === this._currentPage()) return;
    this._currentPage.set(clamped);
    this.loadContracts();
  }

  // ── Sort ──────────────────────────────────────────────────────────────────
  sort(field: SortableField): void {
    if (this._sortField() === field) {
      this._sortDir.set(this._sortDir() === 'asc' ? 'desc' : 'asc');
    } else {
      this._sortField.set(field);
      this._sortDir.set('asc');
    }
  }

  sortIcon(field: SortableField): string {
    if (this._sortField() !== field) return '⇅';
    return this._sortDir() === 'asc' ? '↑' : '↓';
  }

  ariaSort(field: SortableField): string {
    if (this._sortField() !== field) return 'none';
    return this._sortDir() === 'asc' ? 'ascending' : 'descending';
  }

  // ── Preview modal ─────────────────────────────────────────────────────────
  openPreview(row: TableRow): void {
    this.previewRow = row;
    this.activeTab = 'summary';
    this.modalFullscreen = false;
    document.body.style.overflow = 'hidden';
    this.cdr.markForCheck();
  }

  closePreview(): void {
    this.previewRow = null;
    this.modalFullscreen = false;
    document.body.style.overflow = '';
    this.cdr.markForCheck();
  }

  // ── Template helpers ──────────────────────────────────────────────────────
  formatDate(val: string): string {
    if (!val) return '-';
    const d = new Date(val);
    if (isNaN(d.getTime())) return val;
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  }

  formatAutoRenewal(val: boolean | null): string {
    if (val === null) return '-';
    return val ? 'Yes' : 'No';
  }

  truncate(text: string, max: number): string {
    if (!text) return '-';
    return text.length > max ? `${text.slice(0, max)}…` : text;
  }

  getPagerBtnClass(p: number): string {
    if (p === this.currentPage()) return 'ct-pager-btn ct-pager-btn--active';
    if (p === -1) return 'ct-pager-btn ct-pager-btn--ellipsis';
    return 'ct-pager-btn';
  }

  openEdit(row: TableRow): void {
    this.editRow = row;
    this.editForm = {
      terminationDate: row.terminationDate
        ? new Date(row.terminationDate)
        : null,
      expiryDate: row.expiryDate ? new Date(row.expiryDate) : null,
      renewalDate: row.renewalDate ? new Date(row.renewalDate) : null,
      isAutoRenewal:
        row.isAutoRenewal === true
          ? 'yes'
          : row.isAutoRenewal === false
            ? 'no'
            : '',
      comments: row.metadata?.comments // ← read from metadata directly
        ? stripHtml(row.metadata.comments)
        : row.comments || // ← fallback to mapped field
          '',
      // terminationSummary: row.terminationSummary || '',
      terminationSummary: row.metadata?.termination_summary
        ? stripHtml(row.metadata.termination_summary)
        : row.terminationSummary || '',
      autoRenewalSummary: row.autoRenewalSummary || '',
    };
    document.body.style.overflow = 'hidden';
    this.cdr.markForCheck();
  }

  closeEdit(): void {
    this.editRow = null;
    document.body.style.overflow = '';
    this.cdr.markForCheck();
  }

  isEditFormValid(): boolean {
    if (this.editForm.isAutoRenewal === 'yes' && !this.editForm.renewalDate) {
      return false;
    }
    return true;
  }

  saveEdit(): void {
    if (!this.isEditFormValid() || !this.editRow) return;
    this.editSaving = true;
    this.cdr.markForCheck();

    const payload = {
      client_id: String(this.editRow.client_id),
      doc_id: String(this.editRow.id),
      metadata: {
        renewal_date: dateToApiString(this.editForm.renewalDate),
        termination_date: dateToApiString(this.editForm.terminationDate),
        expiry_date: dateToApiString(this.editForm.expiryDate),
        is_auto_renewal: this.editForm.isAutoRenewal,
        comments: this.editForm.comments,
        termination_summary: this.editForm.terminationSummary,
        auto_renewal_summary: this.editForm.autoRenewalSummary,
      },
    };

    this.contractService
      .respositoryMetaUpdate(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.editSaving = false;
          this.closeEdit();
          this.msgService.add({
            severity: 'success',
            summary: 'Updated',
            detail: 'Contract Updated Successfully.',
            life: 4000,
          });
          this.loadContracts();
        },
        error: (error) => {
          this.editSaving = false;
        },
      });
  }
}
